import {
  Avatar,
  Badge,
  Button,
  Card,
  Divider,
  Form,
  InlineGrid,
  InlineStack,
  Icon,
  Link,
  Popover,
  Select,
  SkeletonThumbnail,
  Tag,
  Text,
  TextField,
  Toast,
  BlockStack,
  ExceptionList,
} from '@shopify/polaris';
import StarRating from 'components/starRating';
import QuickUploadImage from 'components/oneclick-upload-image';
import { useCallback, useEffect, useState } from 'react';
import { StatusActiveIcon, AlertCircleIcon, PlusCircleIcon } from '@shopify/polaris-icons';

import helpers from 'helpers';
import dateandtime from 'date-and-time';
import QuickSearchUser from 'components/quickSearchUser';
import __ from 'languages/index';
import { useUpdateReferrer } from 'queries/user_referrer.query';
import { useUpdateUser } from 'queries/users.query';
import { useAddUserSocialProfile, useDeleteUserSocialProfile } from 'queries/user_social_profile.query';
import { useGetFullAddressViaGeo } from 'queries/geographic.query';

export default function UserGeneralInformation({ entity }) {
  const { mutateAsync: getFullAddressViaGeo } = useGetFullAddressViaGeo();

  const { mutateAsync: updateReferrer } = useUpdateReferrer();
  const { mutateAsync: updateEntity, isPending: updating, isSuccess: updateSuccess } = useUpdateUser();

  // user social profile
  const { mutateAsync: addUserSocialProfile, isPending: updating_user_social_profile } = useAddUserSocialProfile();
  const { mutateAsync: deleteUserSocialProfile } = useDeleteUserSocialProfile();

  const [internalErrorNotice, setInternalErrorNotice] = useState('');
  const [srcImageAvatar, setSrcImageAvatar] = useState('');

  useEffect(() => {
    setSrcImageAvatar(entity?.user_avatar);
  }, [entity]);

  const handQuickUpdateSuccess = useCallback(
    async (res: any) => {
      setSrcImageAvatar(res.media_url);
      try {
        await updateEntity({
          user_id: entity?.user_id,
          user_avatar: res.media_url,
        });
      } catch (e) {}
    },
    [entity]
  );

  const handUploadError = useCallback((err: any) => {
    setInternalErrorNotice(err);
  }, []);

  /**
   * Quick note
   */
  const [showQuickNote, setShowQuickNote] = useState(false);
  const [biotext, setBiotext] = useState('');

  const [userAddress, setUserAddress] = useState('');
  const QueryCustomerAddr = useCallback(async () => {
    try {
      let fullAddress = await getFullAddressViaGeo({
        ward: entity?.user_address_ward,
        province: entity?.user_address_city,
      });
      setUserAddress(fullAddress.text);
    } catch (e) {}
  }, [entity]);

  useEffect(() => {
    if (entity) {
      QueryCustomerAddr();
      setBiotext(entity.bio || '');
    }
  }, [entity]);

  useEffect(() => {
    if (updateSuccess === true) {
      setShowQuickNote(false);
    }
  }, [updateSuccess]);

  const saveQuickNoteButtonCallback = useCallback(async () => {
    try {
      await updateEntity({
        user_id: entity?.user_id,
        bio: biotext,
      });
    } catch (e) {}
  }, [entity, biotext]);

  const handleQuickNoteChange = useCallback((newValue: string) => setBiotext(newValue), []);

  /**
   * ENd save quick note
   */

  const [showAddReferrer, setShowAddReferrer] = useState(false);
  const addNewReferrer = useCallback(
    async (referrer_id: bigint | string) => {
      try {
        await updateReferrer({
          customer_id: entity?.user_id,
          referrer_id: referrer_id,
        });
      } catch (e) {}
    },
    [entity?.user_id]
  );

  /**
   * Profile mạng xã hội
   */

  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => setPopoverActive((popoverActive) => !popoverActive), []);
  const [socialName, setSocialName] = useState('facebook');
  const handleSocialNameChange = useCallback((value: string) => setSocialName(value), []);
  const [socialProfileID, setSocialProfileID] = useState('');
  const handleSocialProfileID = useCallback((value: string) => setSocialProfileID(value), []);

  const userSocialProfileRemove = useCallback(async (social_id: string) => {
    await deleteUserSocialProfile(social_id);
  }, []);

  const submitSocialProfile = useCallback(async () => {
    await addUserSocialProfile({
      user_id: entity?.user_id,
      social_name: socialName,
      social_profile_id: socialProfileID,
    });
    setPopoverActive(false);
  }, [entity, socialName, socialProfileID]);

  /**
   * End profile mạng xã hội
   */

  return (
    <>
      {internalErrorNotice && <Toast error content={internalErrorNotice} onDismiss={() => setInternalErrorNotice(null)} />}

      <InlineGrid columns={{ xs: '1', sm: '1', md: '1', lg: ['twoThirds', 'oneThird'] }} gap={'400'}>
        <Card>
          <InlineStack align="start" gap="400" blockAlign="center">
            <div>
              <QuickUploadImage onSuccess={handQuickUpdateSuccess} onError={handUploadError}>
                <Avatar name={entity?.display_name} customer source={helpers.getMediaLink(srcImageAvatar, srcImageAvatar)} size="md" />
              </QuickUploadImage>
            </div>

            <BlockStack gap="100">
              <Text as="h2" variant="headingLg" fontWeight="bold">
                {entity?.user_title && `(${__(entity?.user_title)})`} {entity?.display_name || entity?.user_login}
              </Text>
              <InlineStack gap={'200'}>
                <Text as="p" variant="bodySm" tone="subdued">
                  <Badge>{entity?.user_role}</Badge>
                </Text>
                {entity?.referrer?.referrer_data && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {' - '} Giới thiệu bởi{' '}
                    {entity?.referrer?.referrer_data?.display_name ||
                      entity?.referrer?.referrer_data?.user_email ||
                      entity?.referrer?.referrer_data?.user_phonenumber}
                  </Text>
                )}
                {
                  <Popover
                    active={showAddReferrer}
                    activator={
                      <Button variant="plain" disclosure onClick={() => setShowAddReferrer(true)}>
                        Thêm/sửa người giới thiệu
                      </Button>
                    }
                    autofocusTarget="first-node"
                    onClose={() => setShowAddReferrer(false)}
                  >
                    <Card>
                      <QuickSearchUser
                        title=""
                        user_role=""
                        onClose={(userData: any) => {
                          addNewReferrer(userData.user_id);
                          setShowAddReferrer(false);
                        }}
                      />
                    </Card>
                  </Popover>
                }
              </InlineStack>
              {entity?.user_rate > 0 ? (
                <div>
                  <StarRating num={entity?.user_rate} />
                  <Text as="span" tone="subdued">{`${entity?.user_rate_count} đánh giá`}</Text>
                </div>
              ) : null}
            </BlockStack>
          </InlineStack>

          <br />
          <InlineGrid gap="400" columns={{ xs: 1, sm: 1, md: 1, lg: 4, xl: 4 }} alignItems="center">
            <BlockStack>
              <Text as="p" tone="subdued">
                Khách hàng
              </Text>
              <Text as="p" variant="headingLg">
                {entity?._count?.customers}
              </Text>
            </BlockStack>

            <BlockStack>
              <Text as="p" tone="subdued">
                Đơn hàng
              </Text>
              <Text as="p" variant="headingLg">
                {entity?._count?.orders}
              </Text>
            </BlockStack>

            <BlockStack>
              <Text as="p" tone="subdued">
                Tương tác
              </Text>
              <Text as="p" variant="headingLg">
                {entity?._count?.user_activity}
              </Text>
            </BlockStack>

            <BlockStack>
              <Text as="p" tone="subdued">
                Tham gia
              </Text>
              <Text as="p" variant="headingLg">
                {helpers.subtractDate(entity?.createdAt, 'ngày')}
              </Text>
            </BlockStack>
          </InlineGrid>
          <br />
          <Divider />
          <br />

          <BlockStack gap={'400'}>
            {entity?.user_birthday && (
              <div>
                <Text variant="bodyMd" as="p" fontWeight="bold">
                  Sinh nhật
                </Text>
                <Text as="p">
                  {dateandtime.format(new Date(entity?.user_birthday), 'DD-MM-YYYY')} {'  -  '}{' '}
                  {helpers.getAge(new Date(entity?.user_birthday), ' tuổi')}
                </Text>
              </div>
            )}
            <div>
              <Text variant="bodyMd" as="p" fontWeight="bold">
                Bio
              </Text>
              {showQuickNote ? (
                <>
                  <TextField
                    label=""
                    multiline={2}
                    value={biotext}
                    onChange={handleQuickNoteChange}
                    autoComplete="off"
                    maxLength={250}
                    showCharacterCount
                  />
                  <Button variant="plain" onClick={saveQuickNoteButtonCallback} loading={updating}>
                    Save
                  </Button>
                  &nbsp; or &nbsp;
                  <Button
                    variant="plain"
                    onClick={() => {
                      setShowQuickNote(false);
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Link removeUnderline monochrome onClick={() => setShowQuickNote(true)}>
                  {entity?.bio ?? 'Chưa có giới thiệu bản thân'}
                </Link>
              )}
            </div>
          </BlockStack>
        </Card>
        <Card>
          <Text as="h3" variant="headingMd">
            Thông tin liên hệ
          </Text>
          <BlockStack gap={'200'}>
            <Text variant="bodyMd" as="p" fontWeight="bold">
              Địa chỉ
            </Text>
            <Text as="p" tone="critical">
              {entity?.user_address ?? 'Chưa có thông tin'}
            </Text>
            <Text as="p" tone="critical">
              {userAddress}
            </Text>
          </BlockStack>
          <br />
          <BlockStack gap={'200'}>
            <ExceptionList
              items={[
                {
                  title: 'Email',
                  status: 'warning',
                  icon: entity?.user_verified_email === 1 ? StatusActiveIcon : AlertCircleIcon,
                  description: entity?.user_email ? <Link url="#!">{entity?.user_email}</Link> : 'Chưa có',
                },

                {
                  title: 'Phone number',
                  status: 'warning',
                  icon: entity?.user_verified_email === 1 ? StatusActiveIcon : AlertCircleIcon,
                  description: entity.user_phonenumber ? <Link url={`tel:` + entity?.user_phonenumber}>{entity?.user_phonenumber}</Link> : 'Chưa có',
                },
              ]}
            />
          </BlockStack>

          <br />

          <BlockStack gap={'200'}>
            <Text variant="bodyMd" as="p" fontWeight="bold">
              Mạng xã hội
            </Text>
            <InlineStack align="start" blockAlign="center" gap="200">
              {entity?.user_social_profile &&
                entity?.user_social_profile?.map((el, index) => {
                  let { social_profile_id, social_name, social_id } = el;
                  return (
                    <Tag key={index + '_socialTag'} url={social_profile_id} onRemove={() => userSocialProfileRemove(social_id)}>
                      {social_name}
                    </Tag>
                  );
                })}
              {updating_user_social_profile && <SkeletonThumbnail size="extraSmall" />}

              <Popover
                active={popoverActive}
                activator={
                  <Tag key={'dfhhte'} onClick={() => setPopoverActive(true)}>
                    <InlineStack align="start" blockAlign="center" gap="200">
                      <Icon source={PlusCircleIcon} /> <span>Thêm mạng xã hội</span>
                    </InlineStack>
                  </Tag>
                }
                onClose={togglePopoverActive}
                ariaHaspopup={false}
                sectioned
              >
                <Form onSubmit={submitSocialProfile}>
                  <Select
                    label="Chọn mạng xã hội"
                    options={[
                      { label: 'Facebook', value: 'facebook' },
                      { label: 'Twitter', value: 'twitter' },
                      { label: 'Tiktok', value: 'tiktok' },
                      { label: 'Zalo', value: 'zalo' },
                      { label: 'Skype', value: 'skype' },
                      { label: 'Instagram', value: 'instagram' },
                      { label: 'Pinterest', value: 'pinterest' },
                      { label: 'Youtube', value: 'youtube' },
                      { label: 'Telegram', value: 'telegram' },
                    ]}
                    onChange={handleSocialNameChange}
                    value={socialName}
                  />

                  <TextField label="Liên kết" value={socialProfileID} onChange={handleSocialProfileID} autoComplete="off" />
                </Form>
              </Popover>
            </InlineStack>
          </BlockStack>
        </Card>
      </InlineGrid>
    </>
  );
}

